/**
 * Search-card layout experiment (2026-10-28): on phones, the photo-left card
 * with a facts footer (`control`) vs a photo-on-top card that carries the
 * promo on the photo (`test`), below 650px. Tablets and desktop are unchanged
 * in both arms.
 *
 * The arm is applied as `<html data-card-layout="stacked">` by an inline
 * script in `_document` BEFORE first paint, and the card switches layout in
 * CSS only (`card-stacked:` variant, public/styles/global.css). The search
 * pages are static, so a React-side switch would render `control` first and
 * re-lay out half the mobile visitors after hydration — a layout shift on the
 * site's main landing surface. Same storage key as `useABTest`, so the hook's
 * exposure event reports the arm the script applied.
 *
 * QA: `?card_layout=stacked` / `?card_layout=row` forces (and keeps) an arm.
 */
export const CARD_LAYOUT_EXPERIMENT_ID = 'search_card_stacked'

const STORAGE_KEY = `ab_test_${CARD_LAYOUT_EXPERIMENT_ID}`
const ATTRIBUTE = 'data-card-layout'
/** The card-stacked variant's max width (phones, below `sl:`). */
const STACKED_MAX_WIDTH = 649

/** Runs inline in <head>; must stay dependency-free ES5. */
export const CARD_LAYOUT_BOOT_SCRIPT = `(function(){try{var k='${STORAGE_KEY}',q=location.search.match(/[?&]card_layout=(stacked|row)/),v=q?(q[1]==='stacked'?'test':'control'):localStorage.getItem(k);if(q||(v!=='test'&&v!=='control')){if(!q)v=Math.random()<0.5?'control':'test';localStorage.setItem(k,v)}if(v==='test')document.documentElement.setAttribute('${ATTRIBUTE}','stacked')}catch(e){}})();`

/** The arm a PHONE visitor's search card shows right now, for event
 * properties; `undefined` from 650px up, where both arms render the same
 * card — so `card_layout` on an event always means a phone in that arm. */
export function currentCardLayout(): 'stacked' | 'row' | undefined {
  if (typeof window === 'undefined') return undefined
  if (window.innerWidth > STACKED_MAX_WIDTH) return undefined
  return document.documentElement.getAttribute(ATTRIBUTE) === 'stacked'
    ? 'stacked'
    : 'row'
}
